import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtDate, Loading } from "@/components/common";

const STATUS_LABELS = { nuova: "Nuova", contattato: "Contattato", in_valutazione: "In valutazione", cimitero_attivato: "Cimitero attivato", chiusa: "Chiusa" };
const STATUS_STYLES = {
  nuova: "bg-[#e8f0eb] text-[#2f5d3e]",
  contattato: "bg-[#f0f7ff] text-[#1e3a5f]",
  in_valutazione: "bg-[#fdf3e3] text-[#8a6d2f]",
  cimitero_attivato: "bg-[#4a7c59] text-white",
  chiusa: "bg-[#f1f5f9] text-muted-soft",
};
const WA_LABELS = { sent: "WhatsApp inviato", pending: "WhatsApp in coda", not_configured: "WhatsApp non configurato", error: "WhatsApp non inviato (errore)" };

export default function AdminRichiesteCimiteri() {
  const [data, setData] = useState(null);
  const [cemeteries, setCemeteries] = useState([]);
  const [filter, setFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [notes, setNotes] = useState({});

  const load = () =>
    api.get("/admin/cemetery-requests")
      .then((r) => setData(r.data))
      .catch(() => setData({ requests: [], stats: { nuove: 0, da_contattare: 0, in_valutazione: 0 }, top_cemeteries: [], unread: 0, whatsapp_configured: false }));

  useEffect(() => {
    load();
    api.get("/cemeteries").then((r) => setCemeteries(r.data)).catch(() => {});
    api.post("/admin/cemetery-requests/mark-read").catch(() => {});
  }, []);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/cemetery-requests/${id}`, patch);
      await load();
    } catch { /* noop */ }
  };

  const list = useMemo(() => {
    if (!data) return [];
    if (!filter) return data.requests;
    return data.requests.filter((r) => `${r.cemetery_name} — ${r.comune}`.toLowerCase() === filter);
  }, [data, filter]);

  if (!data) return <Loading />;

  return (
    <div data-testid="admin-cemetery-requests">
      <h2 className="font-serif text-2xl text-navy mb-1">Richieste nuovi cimiteri</h2>
      <p className="text-sm text-muted-soft mb-6">
        Clienti che non hanno trovato il proprio cimitero: ogni richiesta resta salvata singolarmente.
      </p>

      {!data.whatsapp_configured && (
        <div className="rounded-2xl border border-[#e5c07b] bg-[#fdf6e3] p-4 mb-6" data-testid="whatsapp-not-configured">
          <p className="text-sm font-semibold text-[#8a6d2f]">Notifica WhatsApp non configurata</p>
          <p className="text-xs text-body mt-1 leading-relaxed">
            Le richieste vengono registrate regolarmente e compaiono qui, ma la notifica WhatsApp automatica NON viene
            inviata finché non colleghi Twilio WhatsApp Business (variabili TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN o
            TWILIO_API_KEY/SECRET, TWILIO_WHATSAPP_FROM, TWILIO_WHATSAPP_ADMIN_TO nel file .env del backend).
          </p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="card-soft p-4 text-center" data-testid="cemetery-req-stat-nuove">
          <p className="font-serif text-3xl text-navy">{data.stats.nuove}</p>
          <p className="text-xs text-muted-soft">Nuove</p>
        </div>
        <div className="card-soft p-4 text-center" data-testid="cemetery-req-stat-contattare">
          <p className="font-serif text-3xl text-navy">{data.stats.da_contattare}</p>
          <p className="text-xs text-muted-soft">Da contattare</p>
        </div>
        <div className="card-soft p-4 text-center" data-testid="cemetery-req-stat-valutazione">
          <p className="font-serif text-3xl text-navy">{data.stats.in_valutazione}</p>
          <p className="text-xs text-muted-soft">In valutazione</p>
        </div>
      </div>

      {data.top_cemeteries.length > 0 && (
        <div className="mb-6">
          <p className="label-soft mb-2">Cimiteri più richiesti</p>
          <div className="flex flex-wrap gap-2" data-testid="cemetery-req-top">
            <button
              type="button"
              onClick={() => setFilter("")}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${!filter ? "border-[#4a7c59] bg-[#e8f0eb] text-navy" : "border-[#e2e8f0] bg-white text-body"}`}
            >
              Tutte
            </button>
            {data.top_cemeteries.map((t) => {
              const key = `${t.cemetery_name} — ${t.comune}`.toLowerCase();
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`cemetery-req-group-${t.cemetery_name.replace(/\s+/g, "-")}`}
                  onClick={() => setFilter(filter === key ? "" : key)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${filter === key ? "border-[#4a7c59] bg-[#e8f0eb] text-navy" : "border-[#e2e8f0] bg-white text-body"}`}
                >
                  {t.cemetery_name} ({t.comune}) — {t.count} {t.count === 1 ? "richiesta" : "richieste"}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {list.length === 0 ? (
        <p className="text-sm text-muted-soft" data-testid="cemetery-req-empty">Nessuna richiesta ricevuta.</p>
      ) : (
        <div className="space-y-3">
          {list.map((r) => (
            <div
              key={r.id}
              data-testid={`cemetery-req-${r.id}`}
              className={`card-soft p-5 ${r.status === "nuova" ? "border-l-4 !border-l-[#4a7c59]" : ""}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-navy">
                    {r.name} {r.surname}
                    {r.status === "nuova" && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-white bg-[#4a7c59] rounded-full px-2 py-0.5 align-middle">Nuova</span>
                    )}
                  </p>
                  <p className="text-sm text-body">{r.cemetery_name} — {r.comune}</p>
                  <p className="text-xs text-muted-soft mt-0.5">
                    {new Date(r.created_at).toLocaleString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    {" · "}📞 {r.phone} · ✉️ {r.email}
                  </p>
                  {r.message && <p className="text-sm text-body mt-2 italic">“{r.message}”</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <select
                    value={r.status}
                    onChange={(e) => update(r.id, { status: e.target.value })}
                    data-testid={`cemetery-req-status-${r.id}`}
                    className={`input-soft !w-auto text-xs font-bold rounded-full px-3 py-1.5 ${STATUS_STYLES[r.status] || ""}`}
                  >
                    {Object.entries(STATUS_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                  <span className="text-[11px] text-muted-soft" data-testid={`cemetery-req-wa-${r.id}`}>{WA_LABELS[r.whatsapp_status] || r.whatsapp_status}</span>
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => { setOpenId(openId === r.id ? null : r.id); setNotes((n) => ({ ...n, [r.id]: n[r.id] ?? r.notes_internal ?? "" })); }}
                  data-testid={`cemetery-req-detail-${r.id}`}
                  className="text-xs font-semibold text-[#4a7c59] underline underline-offset-2"
                >
                  {openId === r.id ? "Chiudi dettaglio" : "Dettaglio, note e storico"}
                </button>
              </div>

              {openId === r.id && (
                <div className="mt-4 border-t border-[#ece7dd] pt-4 space-y-4" data-testid={`cemetery-req-panel-${r.id}`}>
                  <div>
                    <p className="label-soft">Collega a un cimitero attivato (facoltativo)</p>
                    <select
                      value={r.linked_cemetery_id || ""}
                      onChange={(e) => update(r.id, { linked_cemetery_id: e.target.value })}
                      data-testid={`cemetery-req-link-${r.id}`}
                      className="input-soft w-full"
                    >
                      <option value="">— Nessun collegamento —</option>
                      {cemeteries.map((c) => (
                        <option key={c.id} value={c.id}>{c.name} — {c.comune}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <p className="label-soft">Note interne (visibili solo all'amministratore)</p>
                    <textarea
                      rows={2}
                      className="input-soft w-full"
                      value={notes[r.id] ?? r.notes_internal ?? ""}
                      onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                      data-testid={`cemetery-req-notes-${r.id}`}
                    />
                    <button type="button" onClick={() => update(r.id, { notes_internal: notes[r.id] ?? "" })} data-testid={`cemetery-req-notes-save-${r.id}`} className="btn-secondary !py-2 text-xs mt-2">
                      Salva nota
                    </button>
                  </div>
                  <div>
                    <p className="label-soft">Storico della richiesta</p>
                    <ul className="text-xs text-body space-y-1">
                      {(r.history || []).map((h, i) => (
                        <li key={i}>
                          <span className="font-semibold text-navy">{STATUS_LABELS[h.status] || h.status}</span>
                          {" — "}{fmtDate(h.at)}{h.note ? ` · ${h.note}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
